import {useHistory, useLocation} from 'react-router-dom'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {CPSDP_CONFIGURATION_STORAGE_KEY_PREFIX} from '../../../constants'

/**
 * Utility to get the storage key that matches the provided pathname
 * @param {string} pathname
 * @returns {string}
 */
export const getStoredConfigurationKeyFromPathname = (pathname) => {
    // remove the suffix from pathname
    const idx = pathname.lastIndexOf('.')
    let key = idx > 0 ? pathname.slice(0, idx) : pathname

    // remove the starting slash
    if (key.startsWith('/')) {
        key = key.slice(1)
    }

    return `${CPSDP_CONFIGURATION_STORAGE_KEY_PREFIX}${key}`
}

/**
 * Factory to create a check for valid configuration keys
 * @param [product] - product with setProducts, setAccessories, setExtensions
 * @returns {function(key: string, value: string): boolean} - function that returns true if the provided key,value is a valid configuration key
 */
export const createStoredConfigurationFn = (product) => {
    const availableKeys = new Set([
        // extract available set product ids from the provided product
        ...[
            ...(product?.setProducts || []),
            ...(product?.setAccessories || []),
            ...(product?.setExtensions || [])
        ]
            .map((p) => p?.setProductId)
            .filter(Boolean),
        // virtual set product
        'pid',
        'color', // we know that stokke only uses `color` variation param
        // the engraving set product
        'engraving'
    ])
    return (key, value) =>
        availableKeys.has(key) ||
        // fallback to allow all set product type key/values of the form
        // <product-id>_z##=key=value
        (/^[\w\d]+_\w?\d+$/.test(key) && /.+=.*/.test(value))
}

/**
 * Hook to get stored configuration for customizable product set
 *
 * **Warning:** This hook is tightly coupled with useSelectableSetProduct hook,
 * so any changes should be tested in conjunction with useSelectableSetProduct
 *
 * @param {Object} [options]
 * @param {Object} [product] - product for which we expect to save/load configuration
 * @param {boolean} [options.isEnabled] - should stored configuration be enabled
 * @return {{
 *   isAvailable: boolean,
 *   apply: function(): void,
 *   clear: function(): void,
 * }}
 */
const useStoredConfiguration = ({product, isEnabled = true} = {}) => {
    const history = useHistory()
    const location = useLocation()
    // local storage key for configuration
    const storageKey = useMemo(() => {
        if (!isEnabled) return null

        return getStoredConfigurationKeyFromPathname(location.pathname)
    }, [isEnabled, location.pathname])

    // prepare configuration keys
    const isConfigurationKey = useMemo(() => createStoredConfigurationFn(product), [product])
    // current configuration from search params
    const configuration = useMemo(() => {
        if (!isEnabled) return null
        // parse search params
        const params = new URLSearchParams(location.search)
        // filter configuration params
        const configurationParams = new URLSearchParams(
            [...params.entries()].filter(([key, value]) => isConfigurationKey(key, value))
        )
        return configurationParams.toString()
    }, [isEnabled, location.search, isConfigurationKey])

    // save configuration to local storage when it changes
    useEffect(() => {
        if (configuration && storageKey) {
            localStorage.setItem(storageKey, configuration)
        }
    }, [configuration, history.action, storageKey])

    // Note 1: use state with effect instead of memo to prevent mismatch between ssr and client
    // Note 2: keep the value in an object with pathname as key to not reuse it when pathname changes
    // - this happens when navigating from one product to another but the effect triggers after the render
    // which causes isAvailable to be true for the new product url, but using the old configuration
    const [storedConfigurationForPathname, setStoredConfigurationForPathname] = useState(null)
    useEffect(() => {
        setStoredConfigurationForPathname(
            storageKey ? {[location.pathname]: localStorage.getItem(storageKey)} : null
        )
    }, [location.pathname, storageKey])

    // flag if stored configuration is available
    const isAvailable = Boolean(
        isEnabled && storedConfigurationForPathname?.[location.pathname] && !configuration
    )

    // apply stored configuration
    const apply = useCallback(() => {
        if (isAvailable && storedConfigurationForPathname?.[history.location.pathname]) {
            history.replace(
                `${history.location.pathname}?${
                    storedConfigurationForPathname[history.location.pathname]
                }`,
                history.location.state
            )
        }
    }, [isAvailable, history, storedConfigurationForPathname])

    // clear stored configuration
    const clear = useCallback(() => {
        if (storageKey) {
            localStorage.removeItem(storageKey)
            setStoredConfigurationForPathname(null)
        }
    }, [storageKey])

    return {
        isAvailable,
        apply,
        clear
    }
}

export default useStoredConfiguration
